import React, { KeyboardEvent } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { MenuItem } from '@progress/kendo-react-layout';
import { Component } from '../../../dispatcher/modules';
import Container from '../../layout/container/Container';
import PuzzleButton from '../button/PuzzleButton';
import PuzzleMenu from '../../layout/menu/PuzzleMenu';
import PuzzleFakeCheckbox from './PuzzleFakeCheckbox';
import PuzzleInput from '../fields/input/PuzzleInput';
import {
  MaskedTextBoxChangeEvent
} from '@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps';
import { classNames, Keys } from '@progress/kendo-react-common';
import HBox from '../../layout/container/h_box/HBox';
import {
  PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM,
  PUZZLE_CHECKBOX_MULTISELECT_ON_SELECT_EVENT,
  PUZZLE_CHECKBOX_MULTISELECT_PROPS,
  PUZZLE_CHECKBOX_MULTISELECT_PROPS_DATA,
  PUZZLE_CHECKBOX_MULTISELECT_STATE,
  PUZZLE_CHECKBOX_MULTISELECT_ITEM_RENDERER
} from './Types';
import { popupMenuWrapperHandleTabKeyDown } from '../../../utility';
import * as _ from 'lodash';

const DEFAULT_BUTTON_CONFIG = {
  fillMode: 'outline' as const,
  iconClass: 'tp-icon tp-icon-sm tp-arrow-chevron-down tp-color-accent',
  themeColor: 'primary' as const,
  reverse: true
};

class PuzzleCheckboxMultiselect extends Component<PUZZLE_CHECKBOX_MULTISELECT_PROPS, PUZZLE_CHECKBOX_MULTISELECT_STATE> {
  moduleId = this.props.moduleId;
  ALL_VALUES = 'All items'

  /**
   * Component button, container and div refs
   */
  private readonly buttonRef: React.RefObject<PuzzleButton | null> = React.createRef();
  private anchorRef: React.RefObject<HTMLDivElement> | null = React.createRef();
  private contentRef: React.RefObject<HTMLDivElement> | null = React.createRef();
  private contentMenuRef: React.RefObject<HTMLDivElement> | null = React.createRef();

  state: PUZZLE_CHECKBOX_MULTISELECT_STATE = {
    dropDownVisibility: false,
    data: [],
    search: ''
  };

  constructor(props:PUZZLE_CHECKBOX_MULTISELECT_PROPS) {
    super(props);

    const { data, configListItemAllCheck } = props;
    this.ALL_VALUES = configListItemAllCheck?.value || 'All items';

    this.state = {
      ...this.state,
      data: this.setInitData(data)
    };
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);

    if (prevProps.data !== this.props.data) {
      this.setData(this.props.data);
    }
  }

  /**
   * Create initial data
   * @param {PUZZLE_CHECKBOX_MULTISELECT_PROPS_DATA[]} data
   */
  private setInitData = (data:PUZZLE_CHECKBOX_MULTISELECT_PROPS_DATA[]) => [{
    text: this.translate('allItems'),
    value: this.ALL_VALUES,
    originalIndex: 0,
    selected: false,
    ...this.props.configListItemAllCheck
  }, ...data.map((item, index) => ({
    ...item,
    selected: item.selected ? item.selected : false,
    originalIndex: index + 1
  }))]

  /**
   * Set new data
   * @param {PUZZLE_CHECKBOX_MULTISELECT_PROPS_DATA[]} data
   */
  public setData = (data: PUZZLE_CHECKBOX_MULTISELECT_PROPS_DATA[]):void => {
    this.setState({ data: this.setInitData(data) });
  }

  /**
   * Get data
   */
  public getData = ():PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[] => this.state.data;

  /**
   * Toggles dropdown visibility state.
   * @param {boolean} preventFocus
   */
  private readonly toggleDropDownVisibility = (preventFocus = false) => {
    this.setState(({ dropDownVisibility }: PUZZLE_CHECKBOX_MULTISELECT_STATE) => ({
      dropDownVisibility: !dropDownVisibility,
      search: ''
    }), () => {
      if (preventFocus === false) {
        this.buttonRef.current.buttonEl.focus();
      }
    });
  };

  /**
   * Component drop-down blur event
   * If mouse click target is not contained within component el then drop-down el will be hidden
   * @param {React.FocusEvent<HTMLDivElement>} e Blur event object
   */
  private readonly onBlur = ({ currentTarget, relatedTarget }: React.FocusEvent<HTMLDivElement>) => {
    if (!currentTarget.contains(relatedTarget)) {
      this.setState({
        dropDownVisibility: false
      });
    }
  };

  /**
   * On Escape keyboard button close popup
   * And handle tab and shift + tab keyboard navigation through popup
   * @param {React.KeyboardEvent} event
   */
  private onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.toggleDropDownVisibility();
        break;
      case 'Tab':
        popupMenuWrapperHandleTabKeyDown(event, this.contentRef);
        break;
      default:
        break;
    }
  };

  /**
   * Sort Data
   * @param {PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM} data
   */
  private readonly sortData = (data: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[]) =>
    data.sort((prev: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM, curr: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM) => {
      if (prev.originalIndex > curr.originalIndex) {
        return 1;
      }
      if (prev.originalIndex < curr.originalIndex) {
        return -1;
      }
      return 0;
    })

  /**
   * Handles drop down item click select/unselect
   * The selected item will be pinned to the top of the list among other selected items.
   * The unselected item will be moved to the bottom of the list among unselected items.
   */
  private readonly onSelect = (event:PUZZLE_CHECKBOX_MULTISELECT_ON_SELECT_EVENT):void => {
    const { data: stateData } = this.state;
    const { onChange } = this.props;
    const data = { ...event.item.data };
    data['selected'] = !data.selected;
    const { value, selected } = data;
    const dataIndex = stateData
      .map(item => item.value)
      .indexOf(value);

    // Selected/unselected All items
    if (value === this.ALL_VALUES) {
      let newStateData: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[] = stateData.map(dataItem => ({
        ...dataItem,
        selected
      }));

      if (!selected) {
        newStateData = this.sortData(newStateData);
      }

      if (onChange) {
        const selectedItems = newStateData
          .filter(item => item.selected && item.value !== this.ALL_VALUES)
          .map(dataItem => ({
            text: dataItem.text,
            value: dataItem.value
          }));

        const overrideData = onChange({
          selectedItems,
          isAllSelected: selected
        });

        if (overrideData) {
          overrideData.forEach((overrideDataItem) => {
            newStateData = newStateData.map(dataItem => ({
              ...dataItem,
              ...(dataItem.value === overrideDataItem.value ? {
                ...overrideDataItem
              } : {})
            }));
          });
        }
      }

      this.setState({ data: newStateData });
    } else {
      const { length: stateDataLength } = stateData;
      // Single select item should be pinned
      if (selected) {
        let indexOfLastSelected = stateData
          .map(item => item.selected)
          .lastIndexOf(true);

        if (indexOfLastSelected < 0) {
          indexOfLastSelected = 0;
        }
        stateData.splice(dataIndex, 1);
        stateData.splice(indexOfLastSelected + 1, 0, data);

        const selectedItems = stateData
          .filter(item => item.selected)
          .map(dataItem => ({
            text: dataItem.text,
            value: dataItem.value
          }));

        if (selectedItems.length === stateDataLength - 1) {
          stateData.splice(0, 1, {
            ...stateData[0],
            selected: true 
          });
        }

        if (onChange) {
          onChange({
            selectedItems,
            isAllSelected: false
          });
        }

        this.setState({ data: stateData });
        // Single unselected item should be unpinned and movie on original position
      } else {
        const allItem = { ...stateData[0], selected: false };
        stateData.splice(0, 1);
        let selectedItems = [];
        let unselectedItems = [];

        stateData.forEach((item) => {
          if (item.selected) {
            if (item.value !== value) {
              selectedItems.push(item);
            }
          } else {
            unselectedItems.push(item);
          }
        });

        unselectedItems.push(data);
        unselectedItems = this.sortData(unselectedItems);

        if (onChange) {
          onChange({
            selectedItems,
            isAllSelected: false
          });
        }

        this.setState({
          data: [allItem, ...selectedItems, ...unselectedItems]
        });
      }
    }
  };

  /**
   * Reorder list items
   * @param {number} dataIndex
   * @param {'up' | 'down'} type
   */
  private handleReordering = (dataIndex:number, type: 'up' | 'down'):void => {
    const { data } = this.state;
    const { onReorder } = this.props;
    const isDown = type === 'down';
    const index = isDown ? dataIndex : dataIndex - 1;
    const firstItem = data[isDown ? dataIndex + 1 : dataIndex];
    const secondItem = data[isDown ? dataIndex : dataIndex - 1];

    data.splice(index, 2, firstItem, secondItem); // reorder items in data

    if (onReorder) {
      const newData = data.map((item) => {
        const { value, text, selected } = item;
        return ({
          value,
          text,
          selected
        });
      });

      newData.splice(0, 1);

      const selectedItems = newData
        .filter(item => item.selected)
        .map(item => ({ 
          value: item.value,
          text: item.text
        }));

      onReorder({
        selectedItems,
        data: newData
      });
    }

    this.setState({ data });
  }

  /**
   * Return indexes of filtered data
   */
  private getIndexesOfFilteredData = ():{ startIndex:number, endIndex:number } => {
    const { search, data } = this.state;
    let filteredData = [...data];

    if (search !== '') {
      filteredData = this.handleFilterData(data);
    }

    const selectedArr = filteredData.map((dataItem) => {
      const { selected, value } = dataItem;
      if (selected) {
        return value;
      } else {
        return undefined;
      }
    }).filter(item => !!item);
    const valueArr = data.map(dataItem => dataItem.value);
    const startIndex = valueArr.indexOf(selectedArr[0]);
    const endIndex = valueArr.lastIndexOf(selectedArr[selectedArr.length - 1]);

    return { startIndex, endIndex };
  }

  /**
   * Button menu item renderer
   * All items will be rendered as checkboxes and set in state
   * @param {PUZZLE_CHECKBOX_MULTISELECT_ITEM_RENDERER} dataItem Drop down item props
   */
  private itemRenderer = (dataItem: PUZZLE_CHECKBOX_MULTISELECT_ITEM_RENDERER):JSX.Element | string => {
    const { itemRenderer } = this.props;
    const { data: stateData } = this.state;

    const { selected, text, value, icon } = dataItem.item.data;
    const dataIndex = stateData.map(item => item.value).indexOf(value);
    let { startIndex, endIndex } = this.getIndexesOfFilteredData();

    if (startIndex >= 0 && stateData[startIndex].value === this.ALL_VALUES) {
      startIndex = startIndex + 1;
    }

    if (itemRenderer) {
      return itemRenderer(dataItem, dataIndex);
    }

    const isDisabledUpBtn = dataIndex === startIndex;
    const isDisabledDownBtn = dataIndex === endIndex;

    const additionalButtons = (value !== this.ALL_VALUES && selected) ? (
      <HBox
        className={'tp-checkbox-sorting-arrows'}
      >
        <span
          onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()}
          onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) => e.preventDefault()}
          className={'k-cursor-default'}
        >
          <PuzzleButton
            data-cy={`up-button-${dataIndex}`}
            extraSmall
            fillMode={'flat'}
            iconClass={'tp-icon tp-icon-xs tp-arrow-chevron-up'}
            className={'tp-menu-items-button'}
            disabled={isDisabledUpBtn} // first selected item should have disabled this button
            onClick={() => this.handleReordering(dataIndex, 'up')}
          />
        </span>
        <span
          onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()}
          onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) => e.preventDefault()}
          className={'k-cursor-default'}
        >
          <PuzzleButton
            data-cy={`down-button-${dataIndex}`}
            extraSmall
            fillMode={'flat'}
            className={'tp-menu-items-button'}
            iconClass={'tp-icon tp-icon-xs tp-arrow-chevron-down'}
            disabled={isDisabledDownBtn} // last selected item should have disabled this button
            onClick={() => this.handleReordering(dataIndex, 'down')}
          />
        </span>
      </HBox>
    ) : undefined;

    return (
      <PuzzleFakeCheckbox
        checked={selected}
        label={text}
        additionalIcon={icon}
        additionalButtons={additionalButtons}
      />
    );
  };

  /**
   * Handle reordering selected items with arrow right and arrow left.
   */
  handleKeyDown = (e: KeyboardEvent) => {
    const { target, keyCode } = e;
    const nodeType = _.get(target, 'nodeName', '').toLowerCase();

    if ([Keys.right, Keys.left].includes(keyCode) && nodeType === 'li') {
      const id = _.get(target, 'id', '');
      const indexOf = id.lastIndexOf('_');
      const indexString = id.slice(indexOf + 1);
      const index = parseInt(indexString);

      if (index) {
        const { data } = this.state;
        const isLeft = Keys.left === keyCode;
        const dataItem = data[index];
        const indexOfSecondItem = isLeft ? index - 1 : index + 1;
        const secondItem = data[indexOfSecondItem];

        if (dataItem?.selected && secondItem?.selected && secondItem.originalIndex !== 0) {
          this.handleReordering(index, isLeft ? 'up' : 'down');
        }
      }
    }
  }

  /**
   * Render Menu items
   * @param {PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM} dataItem
   * @param {number} index
   * @param {PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[]} data
   */
  private renderMenuItem = (dataItem: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM, index:number, data:PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[]):JSX.Element => {
    const { text, value, selected = false } = dataItem;
    const { disabledItems = [] } = this.props;
    const menuItemCls = classNames({
      'k-selected': selected,
      'tp-menu': value === this.ALL_VALUES || (selected && !data[index + 1]?.selected),
      'tp-menu-all-items': value === this.ALL_VALUES
    });

    return (
      <MenuItem
        cssClass={menuItemCls}
        data={dataItem}
        disabled={disabledItems.includes(value)}
        key={`grid-config-item-${text}`}
        render={this.itemRenderer}
      />);
  }

  /**
   * Handle change on search
   * @param {MaskedTextBoxChangeEvent} event
   */
  private handleChange = (event: MaskedTextBoxChangeEvent):void => {
    this.setState({ search: event.value });
  }

  /**
   * Clear search
   */
  private onActionButtonClick = () => {
    this.setState({ search: '' });
  }

  /**
   * Filter data
   * @param {PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM} data
   */
   private handleFilterData = (data: PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[]):PUZZLE_CHECKBOX_MULTISELECT_DATA_ITEM[] =>
     data.filter((item) => {
       const text = item.text.toLowerCase();
       return text.startsWith(this.state.search.toLowerCase());
     })

   /**
   * Open Popup
   */
   private openPopup = () => {
     if (!this.state.dropDownVisibility) {
       this.setState({
         dropDownVisibility: true
       });
     }
   }

   /**
   * Open popup on press key Enter or press key space
   * @param {KeyboardEvent} e
   */
   private openPopupWithKeyboard = (e: KeyboardEvent) => {
     const { keyCode } = e;
     if (Keys.enter === keyCode || Keys.space === keyCode) {
       this.openPopup();
     }
   }

  /**
   * Getter for title value inside of button component
   */
  private getButtonTitle = () => {
    const { data } = this.state;
    const { title, customTitle, withCounter = true } = this.props;
    const count = data.filter(item => item.selected && item.value !== this.ALL_VALUES).length;
    if (customTitle) {
      return customTitle(count);
    }
    const counter = withCounter ? ` (${count})` : '';
    return (title ? title : this.translate('selected')) + counter;
  }

  render() {
    const { data, dropDownVisibility, search } = this.state;
    const { dataCy = 'PuzzleButtonComboboxMultiselect', buttonConfig = DEFAULT_BUTTON_CONFIG } = this.props;

    return (
      <Container
        className={'tp-puzzle-multiselect-checkbox'}
        displayInlineFlex
        ref={this.anchorRef}
      >
        <PuzzleButton
          aria-label={this.translate('ariaLabel')}
          data-cy={dataCy}
          onKeyDown={this.openPopupWithKeyboard}
          onMouseDown={this.openPopup}
          ref={this.buttonRef}
          {...buttonConfig}
        >{this.getButtonTitle()}</PuzzleButton>
        <Popup
          className={'tp-puzzle-checkbox-popup'}
          anchor={this.anchorRef.current}
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          popupClass={'tp-menu-with-overflow'}
          show={dropDownVisibility}
        >
          <div
            className="tp-outline-none"
            data-cy={'popup-div'}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            tabIndex={0}
            ref={this.contentRef}
          >
            <PuzzleInput
              placeholder={this.translate('search')}
              size='small'
              onChange={this.handleChange}
              value={search}
              className={'k-my-2 k-mx-1'}
              actionBtnSettings={[{
                buttonType: 'delete',
                'data-cy': 'delete-icon',
                onClick: this.onActionButtonClick
              }]}
            />
            <div
              ref={this.contentMenuRef}
              onKeyDown={this.handleKeyDown}
            >
              <PuzzleMenu
                onSelect={this.onSelect}
                vertical
              >
                {this.handleFilterData(data).map(this.renderMenuItem)}
              </PuzzleMenu>
            </div>
          </div>
        </Popup>
      </Container>
    );
  }
}

export default PuzzleCheckboxMultiselect;
